import mammoth from 'mammoth';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';

export interface ProcessedFile {
  name: string;
  size: number;
  type: string;
  content: string;
  metadata?: {
    pages?: number;
    sheets?: string[];
    encoding?: string;
    wordCount?: number;
    mimeType?: string;
  };
}

export interface FileUploadResult {
  success: boolean;
  file?: ProcessedFile;
  error?: string;
}

// Supported file types
export const SUPPORTED_FILE_TYPES = {
  // Text files
  'text/plain': '.txt',
  'text/csv': '.csv',
  'text/markdown': '.md',
  
  // Documents
  'application/pdf': '.pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
  'application/msword': '.doc',
  
  // Spreadsheets
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'application/vnd.ms-excel': '.xls',
  
  // Code files
  'application/json': '.json',
  'application/javascript': '.js',
  'text/javascript': '.js',
  'text/typescript': '.ts',
  'text/x-python': '.py',
  'text/x-java-source': '.java',
  'text/x-c': '.c',
  'text/x-c++': '.cpp',
  'text/html': '.html',
  'text/css': '.css',
  'application/xml': '.xml',
  'text/xml': '.xml',
  
  // Images (for OCR in future)
  'image/jpeg': '.jpg',
  'image/png': '.png',
  'image/gif': '.gif',
  'image/webp': '.webp'
};

// Maximum file size (10MB)
export const MAX_FILE_SIZE = 10 * 1024 * 1024;

// Check if file type is supported
export const isSupportedFileType = (file: File): boolean => {
  // Check by MIME type
  if (SUPPORTED_FILE_TYPES[file.type as keyof typeof SUPPORTED_FILE_TYPES]) {
    return true;
  }
  
  // Check by file extension
  const extension = '.' + file.name.split('.').pop()?.toLowerCase();
  const supportedExtensions = Object.values(SUPPORTED_FILE_TYPES);
  return supportedExtensions.includes(extension);
};

// Get file extension from name
const getFileExtension = (filename: string): string => {
  return '.' + filename.split('.').pop()?.toLowerCase();
};

// Count words in text
const countWords = (text: string): number => {
  return text.trim().split(/\s+/).filter(word => word.length > 0).length;
};

// Process PDF files
const processPDF = async (file: File): Promise<ProcessedFile> => {
  // For now, return placeholder since pdf-parse has Node.js dependencies
  // In a production environment, you'd handle this server-side
  return {
    name: file.name,
    size: file.size,
    type: file.type,
    content: `[PDF Document: ${file.name}]\n\nThis PDF document has been uploaded. Note: PDF content extraction is performed server-side during analysis for optimal performance. The AI will have access to the full PDF content when processing your request.`,
    metadata: {
      pages: 1 // Placeholder
    }
  };
};

// Process Word documents (.docx)
const processWordDocument = async (file: File): Promise<ProcessedFile> => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    const content = result.value;
    
    return {
      name: file.name,
      size: file.size,
      type: file.type,
      content,
      metadata: {
        wordCount: countWords(content)
      }
    };
  } catch (error) {
    throw new Error(`Failed to process Word document: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

// Process Excel/spreadsheet files
const processSpreadsheet = async (file: File): Promise<ProcessedFile> => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer);
    
    let content = '';
    const sheets: string[] = [];
    
    workbook.SheetNames.forEach((sheetName) => {
      sheets.push(sheetName);
      const worksheet = workbook.Sheets[sheetName];
      const csvData = XLSX.utils.sheet_to_csv(worksheet);
      content += `\n--- Sheet: ${sheetName} ---\n`;
      content += csvData;
      content += '\n';
    });
    
    return {
      name: file.name,
      size: file.size,
      type: file.type,
      content: content.trim(),
      metadata: {
        sheets
      }
    };
  } catch (error) {
    throw new Error(`Failed to process spreadsheet: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

// Process CSV files
const processCSV = async (file: File): Promise<ProcessedFile> => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        try {
          // Convert parsed data back to CSV format for consistent storage
          const csvContent = Papa.unparse(results.data);
          
          resolve({
            name: file.name,
            size: file.size,
            type: file.type,
            content: csvContent,
            metadata: {
              encoding: 'UTF-8'
            }
          });
        } catch (error) {
          reject(new Error(`Failed to process CSV: ${error instanceof Error ? error.message : 'Unknown error'}`));
        }
      },
      error: (error) => {
        reject(new Error(`CSV parsing failed: ${error.message}`));
      }
    });
  });
};

// Process text files
const processTextFile = async (file: File): Promise<ProcessedFile> => {
  try {
    const content = await file.text();
    
    return {
      name: file.name,
      size: file.size,
      type: file.type,
      content,
      metadata: {
        wordCount: countWords(content),
        encoding: 'UTF-8'
      }
    };
  } catch (error) {
    throw new Error(`Failed to process text file: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

// Process image files (convert to base64 for vision models)
const processImage = async (file: File): Promise<ProcessedFile> => {
  try {
    // Convert image to base64 for vision-enabled models
    const arrayBuffer = await file.arrayBuffer();
    
    // Convert ArrayBuffer to base64 without spread operator to avoid stack overflow
    const uint8Array = new Uint8Array(arrayBuffer);
    let binary = '';
    const chunkSize = 8192; // Process in chunks to avoid call stack issues
    for (let i = 0; i < uint8Array.length; i += chunkSize) {
      const chunk = uint8Array.slice(i, i + chunkSize);
      binary += String.fromCharCode.apply(null, Array.from(chunk));
    }
    const base64 = btoa(binary);
    const mimeType = file.type;
    
    return {
      name: file.name,
      size: file.size,
      type: file.type,
      content: `[IMAGE_DATA:${mimeType}:${base64}]`,
      metadata: {
        encoding: 'base64',
        mimeType: mimeType
      }
    };
  } catch (error) {
    // Fallback to descriptive text if base64 conversion fails
    return {
      name: file.name,
      size: file.size,
      type: file.type,
      content: `Image file "${file.name}" has been uploaded. This appears to be a ${file.type} image file. The vision capabilities of the model should allow for image analysis.`,
      metadata: {}
    };
  }
};

// Main file processing function
export const processUploadedFile = async (file: File): Promise<FileUploadResult> => {
  try {
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return {
        success: false,
        error: `File size exceeds the maximum limit of ${Math.round(MAX_FILE_SIZE / (1024 * 1024))}MB`
      };
    }
    
    // Check if file type is supported
    if (!isSupportedFileType(file)) {
      return {
        success: false,
        error: `File type "${file.type}" is not supported. Please upload a supported file type.`
      };
    }
    
    let processedFile: ProcessedFile;
    const extension = getFileExtension(file.name);
    
    // Route to appropriate processor based on file type
    if (file.type === 'application/pdf') {
      processedFile = await processPDF(file);
    } else if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || extension === '.docx') {
      processedFile = await processWordDocument(file);
    } else if (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
               file.type === 'application/vnd.ms-excel' || 
               extension === '.xlsx' || extension === '.xls') {
      processedFile = await processSpreadsheet(file);
    } else if (file.type === 'text/csv' || extension === '.csv') {
      processedFile = await processCSV(file);
    } else if (file.type.startsWith('image/')) {
      processedFile = await processImage(file);
    } else {
      // Default to text processing for other supported types
      processedFile = await processTextFile(file);
    }
    
    return {
      success: true,
      file: processedFile
    };
    
  } catch (error) {
    console.error('File processing error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to process file'
    };
  }
};

// Format file size for display
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Get file type icon
export const getFileTypeIcon = (filename: string): string => {
  const extension = getFileExtension(filename);
  
  switch (extension) {
    case '.pdf': return '📄';
    case '.docx':
    case '.doc': return '📝';
    case '.xlsx':
    case '.xls': return '📊';
    case '.csv': return '📋';
    case '.txt': return '📄';
    case '.json': return '🔧';
    case '.js':
    case '.ts': return '⚡';
    case '.py': return '🐍';
    case '.html': return '🌐';
    case '.css': return '🎨';
    case '.jpg':
    case '.jpeg':
    case '.png':
    case '.gif':
    case '.webp': return '🖼️';
    default: return '📄';
  }
}; 